import React from 'react';
import ReactDOM from 'react-dom';
import { Amplify } from 'aws-amplify';
import { Auth, API, Hub } from 'aws-amplify';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import TagManager from 'react-gtm-module';
import { BrowserRouter as Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import Scrollbar from './components/Scrollbar';
import App from './App';
import store from './store/index';
import * as serviceWorker from './serviceWorker';
import { getTenantNameFromURL } from './components/utility/routeUtil';
import Loading from './components/utility/Loading';

require('dotenv').config();

// import translations
const esTranslations = require(`./locales/es/translation.json`);
const frTranslations = require(`./locales/fr/translation.json`);

const rootElement = document.getElementById('root');
console.log(process.env.REACT_APP_CURRENT_ENVIRONMENT);

if (
  window.location.pathname === '/' ||
  window.location.pathname === '/me3-licensing' ||
  window.location.pathname === '/me3-licensing/' ||
  window.location.pathname === ''
) {
  serviceWorker.unregister();
  if (window.location.hostname !== 'localhost') {
    window.location.reload(true);
  }
} else {
  if (!['local', 'test'].includes(process.env.REACT_APP_CURRENT_ENVIRONMENT)) {
    Sentry.init({
      dsn: 'https://c167acbaeb5c44e5bb0733c61e4d3705@o910590.ingest.sentry.io/5845462',
      environment: process.env.REACT_APP_CURRENT_ENVIRONMENT,
      integrations: [new Integrations.BrowserTracing()],
      tracesSampleRate:
        process.env.REACT_APP_CURRENT_ENVIRONMENT === 'production' ? 0.05 : 1.0,
    });
  }

  window.Auth = Auth;

  const { tenantName } = { ...getTenantNameFromURL() };

  const redirectSignInConfig = `${process.env.REACT_APP_COGNITO_OAUTH_REDIRECT_SIGNIN_BASE_URL}/${tenantName}/${process.env.REACT_APP_COGNITO_OAUTH_REDIRECT_SIGNIN_NESTED_URL}`;
  const redirectSignOutConfig = `${process.env.REACT_APP_COGNITO_OAUTH_REDIRECT_SIGNOUT_BASE_URL}/${tenantName}/${process.env.REACT_APP_COGNITO_OAUTH_REDIRECT_SIGNOUT_NESTED_URL}`;

  Amplify.configure({
    Auth: {
      mandatorySignIn: true,
      region: process.env.REACT_APP_COGNITO_REGION,
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
      identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
      domain: process.env.REACT_APP_COGNITO_DOMAIN,
      responsetype: 'token',
      oauth: {
        domain: process.env.REACT_APP_COGNITO_OAUTH_DOMAIN,
        scope: process.env.REACT_APP_COGNITO_OAUTH_SCOPES.split(','),
        redirectSignIn: redirectSignInConfig,
        redirectSignOut: redirectSignOutConfig,
        responseType: process.env.REACT_APP_COGNITO_OAUTH_RESPONSE_TYPE, // or 'token', note that REFRESH token will only be generated when the responseType is code
        mandatorySignIn: true,
      },
    },
    API: {
      endpoints: [
        {
          name: 'api',
          endpoint: process.env.REACT_APP_API,
        },
      ],
    },
  });

  // Google Tag Manager
  const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GTM,
  };
  TagManager.initialize(tagManagerArgs);

  // initialize i18next with translations
  i18n.use(initReactI18next).init({
    resources: {
      es: {
        translation: esTranslations,
      },
      fr: {
        translation: frTranslations,
      },
    },
    lng: 'en',
    interpolation: { excapeValue: false },
    contextSeparator: '_@context:',
    saveMissing: true,
  });

  window.i18n = i18n;
  ReactDOM.render(
    <Scrollbar
      contentClassName="root-content"
      fullHeight
      noScrollX
      trackYClassName="root-content-scroll-y"
    >
      <Provider store={store.store}>
        <PersistGate
          loading={<Loading height="100vh" />}
          persistor={store.persistor}
        >
          <Router>
            <App />
          </Router>
        </PersistGate>
      </Provider>
    </Scrollbar>,
    rootElement
  );
  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: http://bit.ly/CRA-PWA
  serviceWorker.register();
}
